import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import transportService from '../../services/transportService';
import { FormInput, FormSelect } from '../../components/finance';
import { Plus, Pencil, Trash, ArrowLeft } from '@phosphor-icons/react';
import VehicleForm from '../../components/transport/VehicleForm';
import DialogModal from '../../components/DialogModal';
import './transport.css';

function buildDeleteImpactMessage(impact, noneMessage) {
  let message;
  if (impact.total_to_be_deleted > 0) {
    const deletedLines = Object.entries(impact.will_be_deleted)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message = `This will permanently delete ${impact.total_to_be_deleted} record(s):\n${deletedLines}\n\nThis cannot be undone.`;
  } else {
    message = noneMessage;
  }
  if (Object.keys(impact.will_be_preserved || {}).length > 0) {
    const preservedLines = Object.entries(impact.will_be_preserved)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message += `\n\nThese will be kept, just unlinked:\n${preservedLines}`;
  }
  return message;
}

function VehiclesManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filters, setFilters] = useState({ status: '', vehicle_type: '' });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50 });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, vehicleId: null, message: '', loading: false });

  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'admin' && user.role !== 'school_admin') {
      toast.error('Access denied');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        const params = { ...filters, ...pagination };
        const data = await transportService.listVehicles(params);
        setVehicles(Array.isArray(data) ? data : data.vehicles || []);
      } catch (error) {
        toast.error('Failed to load vehicles: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [filters, pagination]);

  const handleCreate = async (formData) => {
    try {
      await transportService.createVehicle(formData);
      toast.success('Vehicle created successfully');
      setShowModal(false);
      setSelectedVehicle(null);
      const data = await transportService.listVehicles(pagination);
      setVehicles(Array.isArray(data) ? data : data.vehicles || []);
    } catch (error) {
      toast.error('Failed to create vehicle: ' + error.message);
    }
  };

  const handleUpdate = async (id, formData) => {
    try {
      await transportService.updateVehicle(id, formData);
      toast.success('Vehicle updated successfully');
      setShowModal(false);
      setSelectedVehicle(null);
      const data = await transportService.listVehicles(pagination);
      setVehicles(Array.isArray(data) ? data : data.vehicles || []);
    } catch (error) {
      toast.error('Failed to update vehicle: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ isOpen: true, vehicleId: id, message: 'Checking what this will affect...', loading: true });
    try {
      const impact = await transportService.getVehicleDeleteImpact(id);
      const message = buildDeleteImpactMessage(impact, 'No maintenance or attendance records are tied to this vehicle yet — safe to delete.');
      setDeleteDialog({ isOpen: true, vehicleId: id, message, loading: false });
    } catch (error) {
      console.error('Error fetching delete impact:', error);
      setDeleteDialog({ isOpen: true, vehicleId: id, message: 'Delete this vehicle? This cannot be undone.', loading: false });
    }
  };

  const confirmDeleteVehicle = async () => {
    const id = deleteDialog.vehicleId;
    setDeleteDialog({ isOpen: false, vehicleId: null, message: '', loading: false });
    try {
      await transportService.deleteVehicle(id);
      toast.success('Vehicle deleted successfully');
      const data = await transportService.listVehicles(pagination);
      setVehicles(Array.isArray(data) ? data : data.vehicles || []);
    } catch (error) {
      toast.error('Failed to delete vehicle: ' + error.message);
    }
  };

  if (loading) {
    return <Layout><div className="loading">Loading vehicles...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page vehicles-management">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/transport')} title="Back to Transport">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Vehicles Management</h1>
              <p>Add, edit, and manage school vehicles</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => { setSelectedVehicle(null); setShowModal(true); }}>
            <Plus size={18} /> Add Vehicle
          </button>
        </div>

        <div className="filters-section">
          <FormSelect
            label="Status"
            name="status"
            value={filters.status}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPagination({ ...pagination, skip: 0 }); }}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'retired', label: 'Retired' }
            ]}
            placeholder="All Status"
          />

          <FormSelect
            label="Vehicle Type"
            name="vehicle_type"
            value={filters.vehicle_type}
            onChange={(e) => { setFilters({ ...filters, vehicle_type: e.target.value }); setPagination({ ...pagination, skip: 0 }); }}
            options={[
              { value: 'bus', label: 'Bus' },
              { value: 'van', label: 'Van' },
              { value: 'minibus', label: 'Mini Bus' },
              { value: 'shuttle', label: 'Shuttle' }
            ]}
            placeholder="All Types"
          />
        </div>

        <div className="table-section">
          {vehicles.length === 0 ? (
            <div className="empty-state">
              <p>No vehicles found. Add your first vehicle to get started.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Registration</th>
                  <th>Type</th>
                  <th>Make/Model</th>
                  <th>Year</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map(vehicle => (
                  <tr key={vehicle.id}>
                    <td><strong>{vehicle.registration_number}</strong></td>
                    <td>{vehicle.vehicle_type}</td>
                    <td>{vehicle.make} {vehicle.model}</td>
                    <td>{vehicle.year}</td>
                    <td>{vehicle.seating_capacity}</td>
                    <td><span className={`badge badge-${vehicle.status}`}>{vehicle.status}</span></td>
                    <td className="actions">
                      <button className="btn-icon" onClick={() => { setSelectedVehicle(vehicle); setShowModal(true); }} title="Edit">
                        <Pencil size={18} />
                      </button>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(vehicle.id)} title="Delete">
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button disabled={pagination.skip === 0} onClick={() => setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) })}>
            Previous
          </button>
          <span>Page {Math.floor(pagination.skip / pagination.limit) + 1}</span>
          <button disabled={vehicles.length < pagination.limit} onClick={() => setPagination({ ...pagination, skip: pagination.skip + pagination.limit })}>
            Next
          </button>
        </div>

        {showModal && (
          <VehicleForm
            vehicle={selectedVehicle}
            onSave={selectedVehicle ? (id, data) => handleUpdate(id, data) : (_, data) => handleCreate(data)}
            onClose={() => { setShowModal(false); setSelectedVehicle(null); }}
          />
        )}
      </div>

      <DialogModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, vehicleId: null, message: '', loading: false })}
        title="Delete Vehicle"
        message={deleteDialog.message}
        type="warning"
        isLoading={deleteDialog.loading}
        disableCloseWhenLoading={false}
        primaryAction={deleteDialog.loading ? null : { label: 'Delete', onClick: confirmDeleteVehicle }}
        secondaryAction={deleteDialog.loading ? null : { label: 'Cancel', onClick: () => {} }}
      />
    </Layout>
  );
}

export default VehiclesManagementPage;
